import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { startSso, exchangeCode, type ExchangeResult } from "@/sso/pkce";
import { AuthScreen, PrimaryButton, Spinner, ErrorNote } from "@/components/auth/ui";

/** /sso/start — launches the Authorization Code + PKCE self-test (hive-web). */
export function SsoStartPage() {
  useEffect(() => {
    void startSso();
  }, []);
  return (
    <AuthScreen title="Starting single sign-on…">
      <div className="flex justify-center py-4">
        <Spinner size={24} />
      </div>
    </AuthScreen>
  );
}

/** /sso/callback — the demo relying party: exchanges the code and shows claims. */
export function SsoCallbackPage() {
  const [params] = useSearchParams();
  const [result, setResult] = useState<ExchangeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  const code = params.get("code");
  const stateParam = params.get("state");
  const errParam = params.get("error");

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (errParam) {
      setError(params.get("error_description") || errParam);
      return;
    }
    if (!code) {
      setError("No authorization code returned.");
      return;
    }
    const expected = sessionStorage.getItem("sso_state");
    if (expected && stateParam && expected !== stateParam) {
      setError("State mismatch — possible CSRF. Aborting.");
      return;
    }
    exchangeCode(code)
      .then(setResult)
      .catch((e) => setError(e instanceof Error ? e.message : "Exchange failed"));
  }, [code, stateParam, errParam, params]);

  if (error) {
    return (
      <AuthScreen title="Sign-in could not complete" subtitle="The relying-party callback failed.">
        <ErrorNote>{error}</ErrorNote>
        <Link to="/" className="block text-center text-sm text-app-1 hover:underline">
          Back to Hive
        </Link>
      </AuthScreen>
    );
  }

  if (!result) {
    return (
      <AuthScreen title="Completing sign-in…">
        <div className="flex justify-center py-4">
          <Spinner size={24} />
        </div>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen
      title="SSO round-trip complete ✓"
      subtitle="The demo app exchanged the code and validated the ID token."
    >
      <pre className="mb-4 max-h-56 overflow-auto rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] p-3 text-left font-mono text-xs text-app-1">
        {JSON.stringify(result.claims, null, 2)}
      </pre>
      <Link to="/">
        <PrimaryButton type="button">Enter Hive</PrimaryButton>
      </Link>
    </AuthScreen>
  );
}

/** /sso/error — surfaces OAuth errors that couldn't be sent back to an RP. */
export function SsoErrorPage() {
  const [params] = useSearchParams();
  const error = params.get("error") ?? "unknown_error";
  const description = params.get("error_description") ?? "Something went wrong during sign-in.";
  return (
    <AuthScreen title="Sign-in error" subtitle={description}>
      <ErrorNote>
        <span className="font-mono">{error}</span>
      </ErrorNote>
      <Link to="/login" className="block text-center text-sm text-app-1 hover:underline">
        Back to sign in
      </Link>
    </AuthScreen>
  );
}

/** /logout — clears the SSO session and returns to the login screen. */
export function LogoutPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    void logout().finally(() => navigate("/login", { replace: true }));
  }, [logout, navigate]);
  return (
    <AuthScreen title="Signing out…">
      <div className="flex justify-center py-4">
        <Spinner size={24} />
      </div>
    </AuthScreen>
  );
}
